import { SyncOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { Button, Modal, Table, Tag, message } from 'antd';
import { useState } from 'react';
import {
  AiChatSkillSyncCompanyResult,
  AiChatSkillSyncResult,
  aiChatSkillSyncApi,
} from '../../../api/aiAssistant';
import { useCompany } from '../../../hooks/company';

/**
 * 一键把本系统的 AI 助手技能与自定义 System Prompt 同步到「公司配置 → 从其他公司同步数据」
 * 选择的公司。与题库同步不同：目标公司的技能与提示词会被整体覆盖（不合并），
 * LLM Key / 模型等连接配置不受影响。
 */
export default function AiChatSkillSyncButton({ dirty }: { dirty?: boolean }) {
  const { id: companyId } = useCompany();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<AiChatSkillSyncResult>();

  const { loading, run: doSync } = useRequest(() => aiChatSkillSyncApi.sync(companyId), {
    manual: true,
    onSuccess: (data) => {
      setConfirmOpen(false);
      setResult(data);
    },
    onError: (e) => message.error(e.message),
  });

  // 同步接口读取的是服务端已保存的配置：表单有未保存修改时先拦下，
  // 避免用户以为改完即生效、结果把旧配置整体覆盖到目标公司
  const handleOpen = () => {
    if (dirty) {
      message.warning('存在未保存的修改，同步只会下发已保存的配置，请先保存');
      return;
    }
    setConfirmOpen(true);
  };

  return (
    <>
      <Button icon={<SyncOutlined />} onClick={handleOpen}>
        同步到其他公司
      </Button>
      <Modal
        title="同步技能与提示词到其他公司"
        open={confirmOpen}
        confirmLoading={loading}
        okText="开始同步"
        onOk={doSync}
        onCancel={() => setConfirmOpen(false)}
        destroyOnClose
      >
        <p>
          将把本系统的<strong>全部技能与自定义 System Prompt</strong> 同步到「公司配置 →
          从其他公司同步数据」中选择的公司。
        </p>
        <p>
          目标公司原有的技能与 System Prompt 会被<strong>整体覆盖</strong>
          （LLM Key、模型等连接配置不受影响），请确认后操作。
        </p>
        <p>同步内容以最近一次保存为准，页面上未保存的修改不会被同步。</p>
      </Modal>
      <Modal
        title="同步结果"
        open={!!result}
        footer={null}
        width={600}
        onCancel={() => setResult(undefined)}
        destroyOnClose
      >
        <p>
          本次同步 {result?.totalSkills ?? 0} 个技能（
          {result?.customPrompt ? '含自定义 System Prompt' : '未自定义 System Prompt，目标回落内置默认'}
          ），已推送到以下公司：
        </p>
        <Table<AiChatSkillSyncCompanyResult>
          rowKey={(record) => String(record.companyId)}
          size="small"
          pagination={false}
          scroll={{ y: 360 }}
          locale={{ emptyText: '暂无可同步的目标公司' }}
          dataSource={result?.results || []}
          columns={[
            {
              title: '公司',
              dataIndex: 'companyName',
              render: (value, record) => value || `#${record.companyId}`,
            },
            {
              title: '结果',
              render: (_, record) =>
                record.success ? (
                  <Tag color="success">已覆盖</Tag>
                ) : (
                  <Tag color="error">失败：{record.error || '未知错误'}</Tag>
                ),
            },
          ]}
        />
      </Modal>
    </>
  );
}
